import React, { useState } from 'react';
import { X } from 'lucide-react';
import { getPageContent } from '../utils/PageSystem';
import PinEntry from './PinEntry';
import GalaxyVisualization from './GalaxyVisualization';

import ConsciousnessVisualization from './ConsciousnessVisualization';
import DisconnectVisualization from './DisconnectVisualization';
import DungeonVisualization from './DungeonVisualization';
import RealityVisualization from './RealityVisualization';
import RabbitVisualization from './RabbitVisualization';
import RememberVisualization from './RememberVisualization';
import AgrouVisualization from './AgrouVisualization';
import AlyceVisualization from './AlyceVisualization';
import LivingSimulationVisualization from './LivingSimulationVisualization';

import SummitVisualization from './SummitVisualization';

import EmergenceVisualization from './EmergenceVisualization';
import Emergence2Visualization from './Emergence2Visualization';
import DriftVisualization from './DriftVisualization';
import TypewriterVisualization from './TypewriterVisualization';
import HyakkiVisualization from './HyakkiVisualization';
import BasicConsoleVisualization from './BasicConsoleVisualization';
import PuppetMasterVisualization from './PuppetMasterVisualization';
import TachikomaVisualization from './TachikomaVisualization';
import DiveVisualization from './DiveVisualization';
import MessageVisualization from './MessageVisualization';
import Dungeon1Visualization from './Dungeon1Visualization';
import Dungeon2Visualization from './Dungeon2Visualization';
import LegacyVisualization from './LegacyVisualization';
import AlienVisualization from './AlienVisualization';
import BookshelfVisualization from './BookshelfVisualization';
import ChronotunnelVisualization from './ChronotunnelVisualization';
import MengerVisualization from './MengerVisualization';
import FalloutVisualization from './FalloutVisualization';

interface PageOverlayProps {
  pageId: string;
  onClose: () => void;
}

const PageOverlay: React.FC<PageOverlayProps> = ({ pageId, onClose }) => {
  const [verifiedSecret, setVerifiedSecret] = useState(false);
  const content = getPageContent(pageId === 'secret' && verifiedSecret ? 'secretVerified' : pageId);
  
  const handlePinSuccess = () => {
    setVerifiedSecret(true);
  };

  return (
    <div className="fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-70">
      <div className="w-[95%] h-[90%] max-w-4xl max-h-[90vh] flex flex-col bg-[var(--terminal-bg)] border-[5px] sm:border-[15px] border-[var(--terminal-border)] rounded-lg overflow-hidden shadow-[0_0_20px_rgba(95,233,121,0.15),inset_0_0_10px_rgba(0,0,0,0.5)] transition-all duration-500">
        <div className="terminal-titlebar cursor-move flex items-center justify-between">
          <div className="terminal-title text-xs flex-grow text-center">
            {content.title.toUpperCase()} - {pageId.toUpperCase()}.TXT
          </div>
          <div className="terminal-btn terminal-close mr-2" onClick={onClose}>
            <X size={10} className="opacity-0 hover:opacity-100" />
          </div>
        </div>
        
        <div className="page-content p-4 overflow-y-auto crt-flicker flex-grow relative">
          {pageId === 'secret' && !verifiedSecret ? (
            <div className="flex items-center justify-center h-full">
              <PinEntry 
                correctPin="130608" 
                onSuccess={handlePinSuccess}
                onCancel={onClose}
              />
            </div>
          ) : pageId === 'galaxy' ? (
            <GalaxyVisualization />
          ) : pageId === 'consciousness' ? (
            <ConsciousnessVisualization />
          ) : pageId === 'disconnect' ? (
            <DisconnectVisualization />
          ) : pageId === 'dungeon' ? (
            <DungeonVisualization />
          ) : pageId === 'reality' ? (
            <RealityVisualization />
          ) : pageId === 'memory' || pageId === 'chronotunnel' ? (
            <ChronotunnelVisualization />
          ) : pageId === 'menger' ? (
            <MengerVisualization />
          ) : pageId === 'rabbit' ? (
            <RabbitVisualization />
          ) : pageId === 'remember' ? (
            <RememberVisualization />
          ) : pageId === 'agrou' ? (
            <AgrouVisualization />
          ) : pageId === 'simulation' ? (
            <LivingSimulationVisualization />
          ) : pageId === 'alyce' ? (
            <AlyceVisualization />
          ) : pageId === 'summit' ? (
            <SummitVisualization />
          ) : pageId === 'emergence' ? (
            <EmergenceVisualization />
          ) : pageId === 'emergence2' ? (
            <Emergence2Visualization />
          ) : pageId === 'drift' ? (
            <DriftVisualization />
          ) : pageId === 'typewriter' ? (
            <TypewriterVisualization />
          ) : pageId === 'hyakki' ? (
            <HyakkiVisualization />
          ) : pageId === 'basic' ? (
            <BasicConsoleVisualization />
          ) : pageId === 'puppet' ? (
            <PuppetMasterVisualization />
          ) : pageId === 'tachikoma' ? (
            <TachikomaVisualization />
          ) : pageId === 'dive' ? (
            <DiveVisualization />
          ) : pageId === 'message' ? (
            <MessageVisualization />
          ) : pageId === 'dungeon1' ? (
            <Dungeon1Visualization />
          ) : pageId === 'dungeon2' ? (
            <Dungeon2Visualization />
          ) : pageId === 'legacy' ? (
            <LegacyVisualization />
          ) : pageId === 'alien' ? (
            <AlienVisualization />
          ) : pageId === 'books' ? (
            <BookshelfVisualization />
          ) : pageId === 'fallout' ? (
            <FalloutVisualization />
          ) : (
            <div className="page-body mb-4">
              {content.sections.map((section, index) => (
                <div key={index} className="mb-6 border-b border-[var(--terminal-border)] pb-4 last:border-b-0">
                  {section.heading && (
                    <h3 className="text-lg font-bold mb-2 terminal-text">{section.heading}</h3>
                  )}
                  {section.content.map((paragraph, pIndex) => (
                    <p 
                      key={pIndex} 
                      className="mb-3 terminal-text"
                      dangerouslySetInnerHTML={{ __html: paragraph }}
                    />
                  ))}
                </div>
              ))}
            </div>
          )}
          <div className="scanlines absolute inset-0 pointer-events-none"></div>
        </div>
        
        <div className="terminal-footer p-2 text-center text-xs text-[var(--terminal-text)] border-t border-[var(--terminal-border)]">
          PRESS ESC OR CLICK X TO CLOSE
        </div>
      </div>
    </div>
  );
};

export default PageOverlay;